/* @flow */

import type { NETGEM_API_PARENTAL_CONTROL } from '../../libs/netgemLibrary/v8/types/ParentalControl';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import { AvatarSize } from '../../components/profiles/types';
import { showProfilesModal, showProfileSplashscreenModal } from '../../redux/modal/actions/index';
import { deleteWholePlayingHistory } from '../../redux/netgemApi/actions/personalData/playingHistory';
import { getUserProfiles, updateProfiles } from '../../redux/netgemApi/actions/personalData/userProfiles';
import { deleteWholeViewingHistory } from '../../redux/netgemApi/actions/personalData/viewingHistory';
import { deleteWishlist } from '../../redux/netgemApi/actions/personalData/wishlist';
import { getImageUrl } from '../../redux/netgemApi/actions/v8/metadataImage';
import { deleteSearchHistory, reloadSearchHistory, reloadSettings } from '../../redux/ui/actions';
import { fireAndForget } from '../jsHelpers/promise';
import Localizer from '../localization';
import { StorageKeys } from '../localStorage/keys';
import LocalStorageManager from '../localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../messenger';
import { DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES, DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO } from '../parentalControl/constsAndTypes';
import { deleteLocalSettingsForProfile } from '../settings/settings';
import { PROFILE_AVATAR_SIZE } from '../ui/constants';
import { avatarIdsToListOrDefault, checkObsoleteProfiles, createDefaultProfile, createProfile, getAvailableAvatarIds, getRandomUnusedAvatarId, isNameAlreadyUsed } from './helpers';
import { MAX_PROFILE_COUNT, type Profile, type Profiles, type Reconcilier } from './typesAndConstants';

class ProfilesManager {
  static avatarIds: Array<string>;
  static defaultKidsRestrictions: NETGEM_API_PARENTAL_CONTROL | null;
  static defaultRestrictions: NETGEM_API_PARENTAL_CONTROL | null;
  static instance: ProfilesManager;
  static isEnabled: boolean;

  abortController: AbortController;
  currentProfileIndex: number;
  dispatch: Dispatch;
  eTag: string | null;
  getState: () => CombinedReducers;
  profiles: Profiles;

  constructor(
    isProfilesEnabled: boolean,
    avatarIds: string | null,
    defaultRestrictions: NETGEM_API_PARENTAL_CONTROL | null,
    defaultKidsRestrictions: NETGEM_API_PARENTAL_CONTROL | null,
    dispatch: Dispatch,
    getState: () => CombinedReducers,
  ) {
    // DEBUG: remove when profiles are enabled through DMS settings
    const {
      location: { href },
    } = window;
    const u = new URL(href);
    // DEBUG

    ProfilesManager.isEnabled = isProfilesEnabled || u.searchParams.has('profiles');
    ProfilesManager.avatarIds = ProfilesManager.isEnabled ? avatarIdsToListOrDefault(avatarIds) : [];
    ProfilesManager.defaultKidsRestrictions = defaultKidsRestrictions;
    ProfilesManager.defaultRestrictions = defaultRestrictions;

    this.abortController = new AbortController();
    this.currentProfileIndex = -1;
    this.dispatch = dispatch;
    this.eTag = null;
    this.getState = getState;
    this.profiles = { list: [], lockUserProfilesManagement: false };
  }

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'ProfilesManager';
  }

  /*
   * Static functions
   */

  // If lockUserProfilesManagement is not defined, default value is false
  static get isProfileManagementLocked(): boolean {
    const instance = ProfilesManager.getInstance();
    return instance.profiles.lockUserProfilesManagement ?? false;
  }

  // Return last used profile if it still exists
  static get lastProfile(): Profile | null {
    const profileId = LocalStorageManager.loadString(StorageKeys.LastProfileId, '');

    if (profileId === '') {
      return null;
    }

    const profile = ProfilesManager.getInstance().getProfile(profileId);

    if (profile === null) {
      LocalStorageManager.delete(StorageKeys.LastProfileId);
    }

    return profile;
  }

  // Return startup profile if it still exists
  static get startupProfile(): Profile | null {
    const profileId = LocalStorageManager.loadString(StorageKeys.StartupProfileId, '');

    if (profileId === '') {
      return null;
    }

    const profile = ProfilesManager.getInstance().getProfile(profileId);

    if (profile === null) {
      LocalStorageManager.delete(StorageKeys.StartupProfileId);
    }

    return profile;
  }

  static set startupProfile(profile: Profile | null): void {
    LocalStorageManager.save(StorageKeys.StartupProfileId, profile?.id ?? null);
  }

  static getInstance(): ProfilesManager {
    if (!ProfilesManager.instance) {
      throw new Error('Cannot get instance because Profiles Manager has not been initialized');
    }

    return ProfilesManager.instance;
  }

  static initialize: (
    isProfilesEnabled: boolean,
    avatarIds: string | null,
    defaultRestrictions: NETGEM_API_PARENTAL_CONTROL | null,
    defaultKidsRestrictions: NETGEM_API_PARENTAL_CONTROL | null,
    dispatch: Dispatch,
    getState: () => CombinedReducers,
  ) => void = (isProfilesEnabled, avatarIds, defaultRestrictions, defaultKidsRestrictions, dispatch, getState) => {
    ProfilesManager.instance = new ProfilesManager(isProfilesEnabled, avatarIds, defaultRestrictions, defaultKidsRestrictions, dispatch, getState);
  };

  static preloadAvatars = (): void => {
    if (!ProfilesManager.isEnabled) {
      return;
    }

    const instance = ProfilesManager.getInstance();
    const sizePx = PROFILE_AVATAR_SIZE[AvatarSize.Big as string];

    ProfilesManager.avatarIds.forEach((assetId: string) => {
      fireAndForget(instance.dispatch(getImageUrl({ assetId, height: sizePx, width: sizePx })), `ProfilesManager.getImageUrl.${assetId}`);
    });
  };

  static getCurrentProfile = (): Profile | null => {
    const instance = ProfilesManager.getInstance();

    if (instance.currentProfileIndex === -1) {
      return null;
    }

    // Not supposed to be undefined but better sure that sorry
    return instance.profiles.list[instance.currentProfileIndex] ?? null;
  };

  static buildCurrentProfileKey = (key: string): string => {
    if (!ProfilesManager.isEnabled) {
      // Default profile
      return key;
    }

    const instance = ProfilesManager.getInstance();
    const { currentProfileIndex } = instance;
    if (currentProfileIndex === -1) {
      // Default profile
      return key;
    }

    const currentProfile = ProfilesManager.getCurrentProfile();
    if (currentProfile === null) {
      // Default profile
      return key;
    }

    const { id, isDefault } = currentProfile;
    if (isDefault) {
      // Default profile
      return key;
    }

    // Current profile's key
    return `${key}.${id}`;
  };

  static buildProfileKey = (key: string, id?: string): string => (id !== undefined ? `${key}.${id}` : ProfilesManager.buildCurrentProfileKey(key));

  static setETag = (eTag: string | null): void => {
    ProfilesManager.getInstance().eTag = eTag;
  };

  static getProfiles = async (forceRefresh?: boolean): Promise<Profiles> => {
    const instance = ProfilesManager.getInstance();

    if (instance.profiles.list.length === 0 || forceRefresh) {
      let newProfiles: Profiles = { list: [] };
      let newProfile: Profile | null = null;

      const response = await instance.dispatch(getUserProfiles(instance.eTag));

      if (response === null) {
        // Profiles not created yet
        instance.eTag = null;
      } else {
        const { eTag, profiles } = response;

        if (profiles !== undefined) {
          // Profiles modified
          instance.eTag = eTag;
          newProfiles = profiles;
        } else {
          // Profiles not modified
          return instance.profiles;
        }
      }

      if (newProfiles.list === undefined || newProfiles.list.length === 0) {
        // No profiles
        newProfile = createDefaultProfile();
        instance.currentProfileIndex = 0;
        newProfiles = { list: [] };
      } else if (newProfiles.list.every(({ isDefault }) => isDefault === false)) {
        // Some profiles but the default one is missing (shouldn't happen but better safe than sorry)
        newProfile = createDefaultProfile();
      }

      instance.profiles = newProfiles;

      if (newProfile !== null) {
        // Profiles may be modified when saved because of multi-devices conflicts
        const reconcileFn: Reconcilier = (profiles: Profiles) => {
          if (newProfile === null || profiles.list.some(({ isDefault }) => isDefault === true)) {
            // Default profile already exists: no need for a new one
            return null;
          }

          profiles.list.unshift(newProfile);
          return profiles;
        };

        // Apply changes
        const modifiedProfiles = reconcileFn(instance.profiles);
        if (modifiedProfiles !== null) {
          instance.profiles = modifiedProfiles;

          // Save changes
          instance.profiles = await ProfilesManager.saveProfiles(reconcileFn);
        }
      }

      checkObsoleteProfiles(instance.profiles);
    }

    return instance.profiles;
  };

  // Save all profiles to personal data
  static saveProfiles = async (reconcileFn: Reconcilier): Promise<Profiles> => {
    const instance = ProfilesManager.getInstance();
    const profiles = await instance.dispatch(updateProfiles(instance.profiles, reconcileFn, instance.eTag));

    instance.profiles = profiles;

    // Save all profile Ids to compare with list of retrieved profiles at next startup
    LocalStorageManager.save(
      StorageKeys.ProfileIds,
      profiles.list.map(({ id }) => id),
    );

    // Notify all listeners that profiles might have changed
    Messenger.emit(MessengerEvents.PROFILES_UPDATE);

    return profiles;
  };

  static getProfileIndex = (profileId: string | null): number => ProfilesManager.getInstance().profiles.list.findIndex(({ id }) => id === profileId);

  static getProfile = (profileId: string): Profile => {
    const profile = ProfilesManager.getInstance().getProfile(profileId);

    if (profile === null) {
      throw new Error(`Profile "${profileId}" not found`);
    }

    return profile;
  };

  static createNewProfile = (): Profile => {
    const avatarId = getRandomUnusedAvatarId();

    return createProfile(avatarId);
  };

  static setCurrentProfile = (id: string): void => {
    const instance = ProfilesManager.getInstance();

    instance.currentProfileIndex = instance.profiles.list.findIndex((p) => p.id === id);

    const profile = ProfilesManager.getCurrentProfile();

    if (profile !== null) {
      // Save current profile Id into local storage
      LocalStorageManager.save(StorageKeys.LastProfileId, profile.id);
    } else {
      // Delete last profile Id from local storage
      LocalStorageManager.delete(StorageKeys.LastProfileId);
    }

    Messenger.emit(MessengerEvents.CURRENT_PROFILE_UPDATED, profile);
    instance.dispatch(reloadSearchHistory());
    instance.dispatch(reloadSettings());
  };

  static updateProfile = async (profile: Profile): Promise<boolean> => {
    const instance = ProfilesManager.getInstance();

    const reconcileFn: Reconcilier = (profiles: Profiles) => {
      const updatedProfile = profiles.list.find(({ id }) => id === profile.id);

      if (!updatedProfile) {
        // Profile has been deleted on another device
        Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.profile_deleted_on_other_device'));

        // Delete local settings
        deleteLocalSettingsForProfile(profile.id);

        // Check if deleted profile is the current one
        if (profile.id === ProfilesManager.getCurrentProfile()?.id) {
          instance.currentProfileIndex = -1;

          // Show profile selection modal to let user choose another one
          Messenger.once(MessengerEvents.MODAL_CLOSED, () => instance.dispatch(showProfileSplashscreenModal()));
          instance.dispatch(showProfilesModal());
        }

        return null;
      }

      if (updatedProfile.avatarId !== profile.avatarId) {
        // Check if avatar Id is not already used
        if (profiles.list.some(({ avatarId, id }) => avatarId === profile.avatarId && id !== profile.id)) {
          // Avatar already used
          Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.avatar_already_used'));
          return null;
        }
        updatedProfile.avatarId = profile.avatarId;
      }

      if (updatedProfile.name !== profile.name) {
        // Check if name is not already used
        if (isNameAlreadyUsed(profiles, profile.id, profile.name)) {
          return null;
        }
        updatedProfile.name = profile.name;
      }

      if (updatedProfile.pinCode !== profile.pinCode) {
        updatedProfile.pinCode = profile.pinCode;
      }

      if (updatedProfile.isKid !== profile.isKid) {
        updatedProfile.isKid = profile.isKid;
      }

      const { restrictions } = profile;
      const { restrictions: updatedRestrictions } = updatedProfile;
      if (restrictions) {
        // Profile has restrictions
        if (!updatedRestrictions) {
          // Updated profile has no restrictions: set them
          updatedProfile.restrictions = profile.restrictions;
        } else {
          // Updated profile has restrictions: override them
          updatedProfile.restrictions = {
            ...updatedRestrictions,
            ...profile.restrictions,
          };
        }
      }

      return profiles;
    };

    // Apply changes
    const modifiedProfiles = reconcileFn(instance.profiles);

    if (modifiedProfiles === null) {
      return false;
    }

    instance.profiles = modifiedProfiles;

    // Save changes
    instance.profiles = await ProfilesManager.saveProfiles(reconcileFn);

    return true;
  };

  static updateLockUserProfilesManagement = async (lockUserProfilesManagement: boolean): Promise<boolean> => {
    const instance = ProfilesManager.getInstance();

    const reconcileFn: Reconcilier = (profiles: Profiles) => {
      if (profiles.lockUserProfilesManagement === lockUserProfilesManagement) {
        return null;
      }

      profiles.lockUserProfilesManagement = lockUserProfilesManagement;

      return profiles;
    };

    // Apply changes
    const modifiedProfiles = reconcileFn(instance.profiles);

    if (modifiedProfiles === null) {
      return false;
    }

    // Save changes
    instance.profiles = await ProfilesManager.saveProfiles(reconcileFn);

    return true;
  };

  static addProfile = async (newProfile: Profile): Promise<boolean> => {
    const instance = ProfilesManager.getInstance();

    if (instance.profiles.list.length === MAX_PROFILE_COUNT) {
      return false;
    }

    const reconcileFn: Reconcilier = (profiles: Profiles) => {
      if (profiles.list.length === MAX_PROFILE_COUNT) {
        // Maximum number of profiles reached
        Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.max_profiles_reached'));
        return null;
      }

      if (isNameAlreadyUsed(profiles, newProfile.id, newProfile.name)) {
        // Name already used
        return null;
      }

      if (profiles.list.some(({ avatarId, id }) => avatarId === newProfile.avatarId && id !== newProfile.id)) {
        // Avatar already used
        Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.avatar_already_used'));
        return null;
      }

      profiles.list.push(newProfile);

      return profiles;
    };

    // Apply changes
    const modifiedProfiles = reconcileFn(instance.profiles);
    if (modifiedProfiles === null) {
      return false;
    }

    instance.profiles = modifiedProfiles;

    try {
      // Save changes
      const savedProfiles = await ProfilesManager.saveProfiles(reconcileFn);
      instance.profiles = savedProfiles;

      return savedProfiles.list.some(({ id }) => id === newProfile.id);
    } catch (error) {
      // Rollback
      instance.profiles.list = instance.profiles.list.filter(({ id }) => id !== newProfile.id);
      throw error;
    }
  };

  static deleteProfile = async (deletedProfileId: string): Promise<void> => {
    const instance = ProfilesManager.getInstance();

    const reconcileFn: Reconcilier = (profiles: Profiles) => {
      const profileIndex = profiles.list.findIndex(({ id }) => id === deletedProfileId);
      if (profileIndex === -1) {
        // Profile has been deleted on another device
        Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.profile_deleted_on_other_device'));
        return null;
      }

      profiles.list.splice(profileIndex, 1);

      return profiles;
    };

    // Apply changes
    const modifiedProfiles = reconcileFn(instance.profiles);
    if (modifiedProfiles !== null) {
      instance.profiles = modifiedProfiles;

      // Save changes
      instance.profiles = await ProfilesManager.saveProfiles(reconcileFn);
    }

    // Delete associated personal data (no need to await)
    instance.dispatch(deleteWishlist(deletedProfileId));
    instance.dispatch(deleteWholeViewingHistory(deletedProfileId));
    instance.dispatch(deleteWholePlayingHistory(deletedProfileId));
    instance.dispatch(deleteSearchHistory(deletedProfileId));
    deleteLocalSettingsForProfile(deletedProfileId);

    // Check if deleted profile is the current one
    if (deletedProfileId === ProfilesManager.getCurrentProfile()?.id) {
      instance.currentProfileIndex = -1;
    }
  };

  static getAvailableAvatars = (currentAvatarId?: string | null): Array<string> => {
    const instance = ProfilesManager.getInstance();

    return getAvailableAvatarIds(ProfilesManager.avatarIds, instance.profiles.list, currentAvatarId);
  };

  static getUsedAvatarIds = (): Set<string> => {
    const instance = ProfilesManager.getInstance();
    return new Set(instance.profiles.list.map(({ avatarId }) => avatarId).filter((avatarId) => avatarId !== null));
  };

  static checkPinCode = (profileId: string, pinCode: string): { isCorrect: boolean, retryAfter: number } => {
    const profile = ProfilesManager.getProfile(profileId);

    return { isCorrect: profile.pinCode === pinCode, retryAfter: 0 };
  };

  static getMaxAgeAllowedForVideo = (profile: Profile | null): number => profile?.restrictions?.maxAgeAllowedForVideo ?? DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO;

  static getMaxAgeAllowedForGames = (profile: Profile | null): number => profile?.restrictions?.maxAgeAllowedForGames ?? DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES;

  static getMaxAgeAllowedForVideoForCurrentProfile = (): number => ProfilesManager.getMaxAgeAllowedForVideo(ProfilesManager.getCurrentProfile());

  static getMaxAgeAllowedForGamesForCurrentProfile = (): number => ProfilesManager.getMaxAgeAllowedForGames(ProfilesManager.getCurrentProfile());

  static getLockPurchaseForCurrentProfile = (): boolean => ProfilesManager.getCurrentProfile()?.restrictions?.lockPurchase ?? false;

  static getLockedChannelIdsForCurrentProfile = (): Array<string> => ProfilesManager.getCurrentProfile()?.restrictions?.lockedChannelIds ?? [];

  static isKidProfileAvailable = (): boolean => ProfilesManager.defaultKidsRestrictions !== null;

  /*
   * Class functions
   */

  getProfile = (profileId: string): Profile | null => this.profiles.list.find(({ id }) => id === profileId) ?? null;
}

export default ProfilesManager;
